import React, { useState } from 'react';
import axios from 'axios';
import { Plus, X, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AttachmentUploader } from './AttachmentUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function NewTicketDialog({ open, onClose, token, onCreated }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [tagsInput, setTagsInput] = useState('');
  const [privateFields, setPrivateFields] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSubject(''); setDescription(''); setPriority('medium'); setTagsInput('');
    setPrivateFields([]); setAttachments([]);
  };

  const addPrivateField = () => setPrivateFields((prev) => [...prev, { label: '', value: '' }]);
  const updatePrivateField = (idx, key, val) => {
    setPrivateFields((prev) => prev.map((f, i) => (i === idx ? { ...f, [key]: val } : f)));
  };
  const removePrivateField = (idx) => setPrivateFields((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      alert('Please enter a subject and description');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal/tickets`, {
        subject: subject.trim(),
        description: description.trim(),
        priority,
        tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
        attachments,
        private_fields: privateFields.filter((f) => f.label.trim() && f.value.trim()),
      }, { headers: { Authorization: `Bearer ${token}` } });
      reset();
      onCreated(res.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle>New Support Ticket</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary of the issue" data-testid="new-ticket-subject-input" />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue in detail. You can paste a screenshot directly below."
              rows={5}
              data-testid="new-ticket-description-input"
            />
            <div className="mt-2">
              <AttachmentUploader
                uploadEndpoint="/api/portal/tickets/attachments"
                token={token}
                attachments={attachments}
                setAttachments={setAttachments}
                testIdPrefix="new-ticket"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Priority</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="new-ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Tags (comma separated)</label>
              <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="login, billing" data-testid="new-ticket-tags-input" />
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-slate-400" /> Private access fields (optional)
              </label>
              <button type="button" onClick={addPrivateField} className="text-xs font-medium text-[#6e2ea8] flex items-center gap-1" data-testid="add-private-field-btn">
                <Plus className="w-3.5 h-3.5" /> Add field
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              e.g. cPanel login, /administrator URL. Encrypted (AES-256) and visible only to our support team &mdash; not shown back to you once saved.
            </p>
            {privateFields.map((f, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <Input placeholder="Label (e.g. cPanel Login)" value={f.label} onChange={(e) => updatePrivateField(idx, 'label', e.target.value)} data-testid={`private-field-label-${idx}`} />
                <Input placeholder="Value" type="password" value={f.value} onChange={(e) => updatePrivateField(idx, 'value', e.target.value)} data-testid={`private-field-value-${idx}`} />
                <button type="button" onClick={() => removePrivateField(idx)} data-testid={`remove-private-field-${idx}`}>
                  <X className="w-4 h-4 text-slate-400 hover:text-red-500" />
                </button>
              </div>
            ))}
          </div>

          <Button
            className="w-full bg-[#6e2ea8] hover:bg-[#5a2690]"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-new-ticket-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Ticket'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
